import React, { Fragment, useContext, useState } from 'react';
import Menu from '@material-ui/core/Menu';
import styled from 'styled-components';
import { ChartAI as ChartAIIcon } from '@mummssoftware/icons';
import { ConfirmModal } from '@mummssoftware/common-ui';
import {
  GlobalContext,
  OPEN_WIDGET,
} from '@mummssoftware/global-context/GlobalContext';
import { MAIAChatAgent } from '@mummssoftware/utils/constants';
import { Context } from '../../context';
import { StyledActionIcon } from '../../components';
import defaults from '../../translations/en.json';

type MaiaChatAgentRibbonProps = {
  HtmlTooltip: React.ComponentType<any>;
  /** Layout and palette widgets from config-service; without MAIAChatAgent among them the icon only shows a teaser tooltip. */
  widgets: mumms.WidgetConfig[];
};

const WelcomeText = styled.div`
  max-width: 28rem;
  text-align: center;
  & p {
    margin: 0 0 8px;
  }
`;

const Disclaimer = styled.p`
  font-size: 0.85rem;
  opacity: 0.75;
`;

/** Sidebar entry that opens the MAIA Chat Agent after a welcome screen. */
export const MaiaChatAgentRibbon = ({
  HtmlTooltip,
  widgets,
}: MaiaChatAgentRibbonProps) => {
  const [welcomeAnchorEl, setWelcomeAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const { patient } = useContext(Context);
  const globalContext = useContext(GlobalContext);

  const maiaWidget = widgets.find(
    (widget) => widget.componentName === MAIAChatAgent,
  );
  if (!patient?.patientNumber) {
    return null;
  }

  if (!maiaWidget) {
    return (
      <HtmlTooltip title={defaults.maiaTeaserToolTip}>
        {/* Tooltip needs a child that takes its ref and mouse handlers; StyledActionIcon drops them. */}
        <span>
          <StyledActionIcon Icon={ChartAIIcon} id="maia-chat-agent-ribbon" />
        </span>
      </HtmlTooltip>
    );
  }

  const onCloseWelcome = () => {
    setWelcomeAnchorEl(null);
  };

  const onStart = () => {
    setWelcomeAnchorEl(null);
    globalContext.dispatch({
      type: OPEN_WIDGET,
      payload: maiaWidget,
    });
  };

  return (
    <Fragment>
      <HtmlTooltip title={defaults.maiaChatAgentToolTip}>
        <StyledActionIcon
          Icon={ChartAIIcon}
          onClick={(event: React.MouseEvent<HTMLElement>) =>
            setWelcomeAnchorEl(event.currentTarget)
          }
          id="maia-chat-agent-ribbon"
        />
      </HtmlTooltip>
      <Menu
        id="maia-chat-agent-welcome"
        anchorEl={welcomeAnchorEl}
        open={Boolean(welcomeAnchorEl)}
        onClose={onCloseWelcome}
        autoFocus
      >
        <ConfirmModal
          modalText={defaults.maiaWelcomeHeading}
          content={
            <WelcomeText>
              <p>{defaults.maiaWelcomeSubheading}</p>
              <Disclaimer>{defaults.maiaChargesDisclaimer}</Disclaimer>
            </WelcomeText>
          }
          okText={defaults.start}
          noText={defaults.cancel}
          onOKClick={onStart}
          onCancelClick={onCloseWelcome}
        />
      </Menu>
    </Fragment>
  );
};

export default MaiaChatAgentRibbon;
